import { useRef, useState } from 'react';
import { Upload, Download, Trash2, File as FileIcon, Image as ImageIcon } from 'lucide-react';
import useCardPanelStore from '../../../store/cardPanelStore';
import useAuthStore from '../../../store/authStore';
import useBoardCan, { useHasServerAccess } from '../../../hooks/useBoardCan';
import formatRelative, { formatAbsolute } from '../../../utils/formatRelative';
import { dialog } from '../../../store/dialogStore';
import { isOrgAdmin } from '../../../utils/permissions';

function humanSize(bytes) {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
}

export default function FilesTab({ cardId }) {
  const { attachments, loadingAttachments, uploadAttachment, deleteAttachment, uploadProgress } = useCardPanelStore();
  const currentUser = useAuthStore((s) => s.user);
  // Enviar anexo nunca teve gate no comportamento atual — fallback preserva
  // isso (sempre liberado); so passa a exigir edit_content quando o backend
  // novo manda my_access de fato. Apagar anexo de outra pessoa continua so
  // por isOrgAdmin, sem depender de edit_content (regra atual, inalterada).
  const can = useBoardCan();
  const hasServerAccess = useHasServerAccess();
  const canEditContent = hasServerAccess ? can.edit_content : true;
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [err, setErr] = useState(null);

  const handleFiles = async (files) => {
    setErr(null);
    for (const file of files) {
      try {
        await uploadAttachment(cardId, file);
      } catch (e) {
        setErr(e.message);
      }
    }
  };

  const onPick = (e) => {
    if (e.target.files?.length) handleFiles([...e.target.files]);
    e.target.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files?.length) handleFiles([...e.dataTransfer.files]);
  };

  const handleDownload = async (a) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`/api/v1/attachments/${a.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { dialog.alert('Erro', 'Falha no download.'); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = a.original_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (a) => {
    if (!(await dialog.confirm(`Excluir "${a.original_name}"`, 'Esta ação não pode ser desfeita.', { confirmLabel: 'Excluir', danger: true }))) return;
    try { await deleteAttachment(a.id); }
    catch (e) { dialog.alert('Erro', e.message); }
  };

  const isImage = (mime) => mime?.startsWith('image/');

  return (
    <div className="flex flex-col h-full">
      {canEditContent && (
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`m-5 border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
          dragging ? 'border-accent bg-accent/5' : 'border-border'
        }`}
      >
        <Upload className="w-8 h-8 text-text-secondary mx-auto mb-2" />
        <p className="text-sm text-text-primary mb-1">Arraste arquivos aqui ou</p>
        <button
          onClick={() => inputRef.current?.click()}
          className="text-sm text-accent hover:underline"
        >
          escolha do computador
        </button>
        <input ref={inputRef} type="file" multiple onChange={onPick} className="hidden" />
        <p className="text-[11px] text-text-secondary mt-2">Qualquer tipo de arquivo • até 100MB</p>
        {uploadProgress != null && (
          <div className="mt-3">
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full bg-accent transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
            <p className="text-[11px] text-text-secondary mt-1">{uploadProgress}%</p>
          </div>
        )}
        {err && <p className="text-xs text-danger mt-2">{err}</p>}
      </div>
      )}

      <div className="flex-1 overflow-y-auto px-5 pb-5">
        {loadingAttachments ? (
          <div className="text-center py-6 text-sm text-text-secondary">Carregando...</div>
        ) : attachments.length === 0 ? (
          <div className="text-center py-6 text-sm text-text-secondary">Nenhum arquivo ainda.</div>
        ) : (
          <div className="space-y-2">
            {attachments.map((a) => (
              <div key={a.id} className="flex items-center gap-3 bg-card border border-border rounded-lg p-3 group">
                <div className="w-10 h-10 rounded bg-accent/10 text-accent flex items-center justify-center shrink-0">
                  {isImage(a.mime_type) ? <ImageIcon className="w-5 h-5" /> : <FileIcon className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-text-primary truncate">{a.original_name}</div>
                  <div className="text-[11px] text-text-secondary">
                    {humanSize(a.size)} • {a.uploader_name || 'Usuário'} •{' '}
                    <span title={formatAbsolute(a.created_at)}>{formatRelative(a.created_at)}</span>
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(a)}
                  className="p-2 rounded hover:bg-white/5 text-text-secondary hover:text-text-primary"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </button>
                {((a.uploaded_by === currentUser?.id && canEditContent) || isOrgAdmin(currentUser)) && (
                  <button
                    onClick={() => handleDelete(a)}
                    className="p-2 rounded hover:bg-danger/10 text-text-secondary hover:text-danger"
                    title="Excluir"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
